/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useMemo } from "react"
import { useSelector } from "react-redux"
import { AppState } from "store/store.type"
import { useMartItemMenuDisplayV2Query } from "queries"
import { useEnablePartialMenu } from "hooks/useEnablePartialMenu"
import {
  getItemWithoutPhotoCount,
  getItemWithoutPhotoCountFromDepartment,
} from "utils/menu_helper"
import { mapItemDepartmentsToDepartments } from "utils/menuV3Mapper"

interface UseItemsWithoutPhotoCountOptions {
  /**
   * Skip the catalogue fetch when the caller already knows it will not show the
   * count. Worth passing: under partial menu this hook is the only reason the
   * full catalogue is loaded on menu open.
   */
  skip?: boolean
}

/**
 * Number of items missing a photo for the store currently being edited.
 *
 * Legacy menus derive this from `state.menu.items`, which is only populated by
 * FETCH_MENU / FETCH_CATALOGUE. Partial menu skips that fetch entirely (see the
 * SKIP_FETCH branch in MenuEditor), so `items` stays empty and the count always
 * came out as 0 - which silently hid the photo coverage banner.
 *
 * Follows the same query-or-Redux shape as the useMenuOverview* hooks, reusing
 * their mapper and count helper so the banner agrees with what the "items
 * without photo" filter lists. The query args match theirs on purpose, so the
 * filter path reuses this cache entry instead of refetching.
 *
 * TEMPORARY: recovering the count this way loads the whole catalogue, which is
 * the load partial menu exists to avoid. The merchant-group photo-count
 * endpoint cannot replace it because it is forbidden for single-store users.
 * Drop this once BE exposes `totalItemsWithoutPhoto` on /v3/menu/hierarchies,
 * the same way `totalHiddenItems` was added.
 *
 * Known cost of that workaround, accepted for now:
 * - the catalogue is fetched on every fresh menu-editor load. The `skip` below
 *   only helps within a session, since `isItemPhotoCoverageBannerSkipped` is
 *   Redux-only and resets on reload.
 * - this subscription stays live while the Overview tab is mounted, and
 *   `martItemMenuDisplayV2` is in PARTIAL_MENU_REFRESH_TAGS (plus invalidated
 *   directly by ItemAvailabilityDropdownDuxton), so each menu mutation
 *   refetches the catalogue. Latching the resolved count and then skipping
 *   would drop the subscription, at the price of a stale count after edits.
 */
export const useItemsWithoutPhotoCount = ({
  skip = false,
}: UseItemsWithoutPhotoCountOptions = {}): number => {
  const enablePartialMenu = useEnablePartialMenu()

  const { items, merchantID, merchantGroupID } = useSelector(
    (state: AppState) => state.menu,
  )

  // `currentData`, not `data`: RTK Query keys the cache per query args, and
  // `data` keeps returning the previous store's catalogue until the new
  // merchantID's request resolves. `currentData` is undefined across that
  // switch, which is what lets the guard below fail closed. Same reason
  // MenuOverviewDepartmentsV3 / MenuOverviewSubDepartmentsV3 use it.
  const { currentData: martMenuData } = useMartItemMenuDisplayV2Query(
    {
      merchantID,
      merchantGroupID,
    },
    {
      skip: !enablePartialMenu || !merchantID || skip,
    },
  )

  return useMemo(() => {
    if (enablePartialMenu) {
      // Fail closed while this store's catalogue is unavailable - in flight,
      // errored, or mid store switch. `state.menu.items` is not the source of
      // truth here and CLEAR_MENU does not reset it, so a previous store's items
      // would survive the switch and the banner would report that store's count.
      // Same reasoning as the enablePartialMenu / enableBcrsMenuGroupV2 resets
      // in CLEAR_MENU.
      if (!martMenuData) {
        return 0
      }

      return mapItemDepartmentsToDepartments(
        martMenuData.departments || [],
      ).reduce(
        (total, department) =>
          total + getItemWithoutPhotoCountFromDepartment(department),
        0,
      )
    }

    return getItemWithoutPhotoCount(items)
  }, [enablePartialMenu, martMenuData, items])
}
